import { useEffect, useState } from "react";
import { ZoomClient } from "../index-types";
import {
  ZOOM_CLIENT_INIT_DEPENDENT_ASSETS,
  ZOOM_CLIENT_INIT_LANGUAGE,
  ZOOM_CLIENT_INIT_OPTIONS,
} from "../zoom-constant";

type ZoomVideoSdk = typeof import("@zoom/videosdk");

/**
 * Loads the Zoom SDK off the startup path and initialises one client for the
 * session. Consumers already treat a null client as "not ready yet".
 */
export const useZoomClient = (): ZoomClient | null => {
  const [zmClient, setZmClient] = useState<ZoomClient | null>(null);

  useEffect(() => {
    let isCancelled = false;
    let sdk: ZoomVideoSdk | null = null;

    const initializeZoomClient = async () => {
      try {
        sdk = await import("@zoom/videosdk");
        if (isCancelled) return;

        const client = sdk.default.createClient();
        await client.init(
          ZOOM_CLIENT_INIT_LANGUAGE,
          ZOOM_CLIENT_INIT_DEPENDENT_ASSETS,
          ZOOM_CLIENT_INIT_OPTIONS
        );

        if (!isCancelled) setZmClient(client);
      } catch (error) {
        // A failed warm-up leaves the client null; the call screens surface that state themselves.
        console.error("Failed to initialise the Zoom client", error);
      }
    };

    void initializeZoomClient();

    return () => {
      isCancelled = true;
      sdk?.default.destroyClient();
    };
  }, []);

  return zmClient;
};
